// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Async PDF blob assemblers for the flight report (V1 and V2 layouts),
// plus the renderAiSummarySection writer helper used by V2.
//
// V2 is the current default (buildFlightReportPdfBlob → V2). V1 is the
// legacy layout retained for reference and triggered by older entry
// points. Both walk a makePdfWriter() instance through cover, summary,
// chart-data, finding, cylinder-summary, and source sections — the data
// for each section comes from the report-builders helpers and the
// engine-review model.
//
// Exposed as a factory so the binding stays alive against the hoisted
// helpers and the mutable healthSummary / flightData / selectedFlight /
// sourceName / sourceMonitor / sourceRows / sourceRawRows / aiSummaryState
// globals.

import { writePropellerRpmContextSection } from "./pdf-sections.js";
import {
  reportMetricMentionableInPdf,
} from "./report-model.js";
import { writeFuelPressureContextSection } from "./pdf-sections.js";
import {
  assertFlightReportContextCurrent,
  captureFlightReportContext,
} from "./report-context.js";

export function createFlightReportBlob({
  // state getters
  getHealthSummary,
  getFlightData,
  getSelectedFlight,
  getSourceMonitor,
  getSourceName,
  getSourceRows,
  getSourceRawRows,
  getAiSummaryState,
  getSavedSpecialTestResults = () => [],
  gamiSweepEngineIdentity = () => ({ engineId: "engine1", engineLabel: "Engine" }),
  gamiSweepPeakDisplayRows = (result) => Array.isArray(result?.peaks) ? result.peaks : [],
  buildReportOverviewVisuals = () => [],
  // analysis + finding helpers
  calculateHealthSummary,
  currentEngineDescriptors,
  engineReviewItems,
  engineMetricLesson,
  engineTriageStatus,
  flightDateLabel,
  formatPointTime,
  formatCompactElapsedTime,
  aiSummaryPayloadJson,
  aiSummaryGeneratedLabel,
  reportFindingText,
  reportFindingEvidenceItems,
  reportWindowSummary,
  findingDisplayLabel,
  findingCausePresentation,
  phaseProfileRows,
  operationalProfileReportRows,
  dataQualityItems,
  rawChannelKeys,
  scoringRole,
  writePrimaryFindingSections,
  // pdf writer + blob helpers
  makePdfWriter,
  buildPdfBlob,
  reportFileName,
  // report-builders helpers
  flightReportRows,
  reportFlightMapImage,
  reportFlightMapCaption,
  reportFlightMapTiles,
  reportFlightProfileRows,
  reportMultiEngineCards,
  reportMultiEngineOverviewRows,
  reportMultiEnginePrimaryCues,
  reportMultiEngineTraceRows,
  reportTraceRows,
  reportTurboContext,
  reportHealthCheckRows,
  reportCylinderRows,
  reportCylinderTableRows,
  reportEngineMetricRows,
  reportAtAGlanceCards,
  reportSensorChannelItems,
  sortReportActionsByPriority,
  // constants (getters so the binding doesn't fail at factory-call time if
  // the surrounding module hasn't bound the constant yet)
  getProfileDurationBuckets,
  getReportDisclaimerParagraphs,
}) {
  function reportReviewItems(summary) {
    return engineReviewItems(summary)
      .filter((item) => reportMetricMentionableInPdf(item.key, item.metric));
  }

  function aiSummaryItemMentionsSuppressedMag(item, healthSummary) {
    if (reportMetricMentionableInPdf("mag", healthSummary?.mag)) return false;
    const evidenceKeys = Array.isArray(item?.evidenceKeys) ? item.evidenceKeys : [];
    if (evidenceKeys.some((key) => String(key ?? "").split(".")[0] === "mag")) return true;
    const text = [item?.title, item?.text, item?.label, item?.value, item?.detail]
      .map((value) => String(value ?? ""))
      .join(" ");
    return /\bmag(?:neto)?(?:\s|-)?check\b|\bmagneto\b/i.test(text);
  }

  function renderAiSummarySection(writer, summary, generatedLabel, options = {}) {
    if (!summary) return;

    const arr = (value) => (Array.isArray(value) ? value : []);
    const visibleItems = (items) => arr(items)
      .filter((item) => !aiSummaryItemMentionsSuppressedMag(item, options.healthSummary));
    const overview = aiSummaryItemMentionsSuppressedMag({ text: summary.overview }, options.healthSummary)
      ? ""
      : String(summary.overview ?? "").trim();

    const itemRows = (items) => visibleItems(items)
      .map((item) => ({
        title: String(item?.title ?? "").trim(),
        body: String(item?.text ?? "").trim(),
      }))
      .filter(({ title, body }) => title || body);

    const evidenceRows = (items) => visibleItems(items)
      .map((item) => {
        const label = String(item?.label ?? "").trim();
        const value = String(item?.value ?? "").trim();
        const detail = String(item?.detail ?? "").trim();
        const head = label && value ? `${label}: ${value}` : (label || value);
        return { title: head, body: detail };
      })
      .filter(({ title, body }) => title || body);

    const renderRows = (headingText, rows) => {
      if (!rows.length) return;
      writer.heading(headingText);
      rows.forEach(({ title, body }, index) => {
        if (title) writer.subheading(title);
        if (body) writer.paragraph(body);
        if (index < rows.length - 1) writer.gap(4);
      });
    };

    writer.heading("AI Summary");
    if (generatedLabel) {
      writer.keyValue("Generated", generatedLabel);
      writer.gap(2);
    }
    if (overview) writer.paragraph(overview);

    renderRows("What Stands Out", itemRows(summary.whatStandsOut));
    renderRows("Supporting Evidence", evidenceRows(summary.supportingEvidence));
    renderRows("Operating Context", itemRows(summary.operatingContext));
    renderRows("Caveats", itemRows(summary.caveats));
    renderRows("Suggested Review Focus", itemRows(summary.suggestedReviewFocus));

    writer.gap(6);
  }

  function reportStandalonePrimaryFinding(summary) {
    return {
      title: "No standalone engine review cue",
      meta: Number.isFinite(summary?.overall?.score)
        ? `Overall health ${summary.overall.score}/100 - ${summary.overall.label ?? "reviewed"}`
        : "Context-only cues may still be present",
      body: "No engine-specific finding is active at this sensitivity setting. Context events such as transient divergence or descent EGT dropout remain available in the chart and supporting context, but they are not treated as standalone scored report findings.",
      causes: [
        ["No standalone scored engine cue is active", 0.7],
        ["Review context windows if the trace shape repeats", 0.3],
      ],
      actions: [
        ["low", "Use the chart context and trend history before assigning maintenance significance"],
      ],
      confidence: "Low",
    };
  }

  // Every generation runs against one frozen snapshot of the app state (issue
  // #480): the selected flight, its samples, source labels, AI summary, and
  // saved special tests are captured before the first await and read from the
  // snapshot afterwards, never from the live globals. The single await (the
  // map render) is followed by a superseded check so a flight switch during
  // that window aborts instead of producing a mixed-identity report.
  function captureAssemblyContext() {
    const context = captureFlightReportContext({ getSelectedFlight, getFlightData, getAiSummaryState });
    const savedSpecialTests = getSavedSpecialTestResults();
    return {
      ...context,
      sourceMonitor: getSourceMonitor(),
      sourceName: getSourceName(),
      sourceRows: getSourceRows(),
      sourceRawRows: getSourceRawRows(),
      aiSummaryState: getAiSummaryState(),
      healthSummary: getHealthSummary(),
      savedSpecialTests: Array.isArray(savedSpecialTests) ? savedSpecialTests.slice() : [],
    };
  }

  function assertAssemblyContextCurrent(context) {
    assertFlightReportContextCurrent(context, { getSelectedFlight, getFlightData });
  }

  function reportIdentity(context, blob, fileName) {
    return {
      blob,
      fileName,
      flightId: context.flightId,
      databaseFlightId: context.databaseFlightId,
      analysisRunId: context.analysisRunId,
      aiSummaryGeneratedAt: context.aiSummaryGeneratedAt,
      aircraft: context.aircraft,
      generatedAt: new Date().toISOString(),
    };
  }

  // Map an elapsed second back to the nearest logged sample's wall-clock time,
  // mirroring the on-screen panel. Empty string when no usable timestamp.
  function clockTextForElapsed(elapsedSeconds, data = getFlightData()) {
    if (!Number.isFinite(elapsedSeconds)) return "";
    let nearest = null;
    let nearestDistance = Infinity;
    for (const point of Array.isArray(data) ? data : []) {
      if (!Number.isFinite(point?.elapsedSeconds)) continue;
      const distance = Math.abs(point.elapsedSeconds - elapsedSeconds);
      if (distance < nearestDistance) {
        nearest = point;
        nearestDistance = distance;
      }
    }
    const clock = nearest ? formatPointTime(nearest) : "";
    return clock && clock !== "--" ? clock : "";
  }

  // Window endpoints show wall-clock with elapsed for context: "2:34:00 PM (03:00)".
  function gamiWindowEndpointText(elapsedSeconds, data) {
    const elapsed = formatCompactElapsedTime(elapsedSeconds);
    const clock = clockTextForElapsed(elapsedSeconds, data);
    return clock ? `${clock} (${elapsed})` : elapsed;
  }

  function titleCaseConfidence(value) {
    const text = String(value ?? "").trim();
    if (!text) return "";
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  }

  // Per-cylinder peak rows for the PDF table, sorted richest-first to mirror
  // the on-screen peak table (Cyl / Peak FF / Peak EGT / Time / Cue).
  function gamiPeakTableRows(result, data) {
    const peaks = gamiSweepPeakDisplayRows(result, currentEngineDescriptors());
    return peaks
      .slice()
      .sort((a, b) => {
        const fa = Number(a?.peakFuelFlowGph);
        const fb = Number(b?.peakFuelFlowGph);
        if (Number.isFinite(fa) && Number.isFinite(fb) && fa !== fb) return fb - fa;
        return (a?.displayCylinder ?? a?.cylinder ?? 0) - (b?.displayCylinder ?? b?.cylinder ?? 0);
      })
      .map((peak) => {
        const cyl = peak?.displayCylinderLabel ?? (peak?.cylinder ? `#${peak.cylinder}` : (peak?.key ?? "--"));
        const ff = Number.isFinite(peak?.peakFuelFlowGph) ? `${Number(peak.peakFuelFlowGph).toFixed(2)} GPH` : "--";
        const egt = Number.isFinite(peak?.peakEgtF) ? `${Math.round(peak.peakEgtF)} F` : "--";
        const time = Number.isFinite(peak?.peakElapsedSeconds)
          ? (clockTextForElapsed(peak.peakElapsedSeconds, data) || formatCompactElapsedTime(peak.peakElapsedSeconds))
          : "--";
        const cue = peak?.clear ? "Clear" : (peak?.reason || "Limited");
        return [cyl, ff, egt, time, cue];
      });
  }

  function gamiSpecialTestReportRows(resultRow, data) {
    const result = resultRow?.result_json ?? resultRow?.result ?? {};
    const identity = gamiSweepEngineIdentity(resultRow, currentEngineDescriptors());
    const startTime = Number(resultRow?.start_time ?? result.startTime);
    const endTime = Number(resultRow?.end_time ?? result.endTime);
    const windowText = Number.isFinite(startTime) && Number.isFinite(endTime)
      ? `${gamiWindowEndpointText(startTime, data)} - ${gamiWindowEndpointText(endTime, data)}`
      : "Marked window saved";
    const spreadText = Number.isFinite(result.spreadGph)
      ? `${Number(result.spreadGph).toFixed(2)} GPH`
      : "Unavailable";
    const peaksText = Number.isFinite(result.clearPeakCount) && Number.isFinite(result.peakCount)
      ? `${result.clearPeakCount}/${result.peakCount} clear peaks`
      : "Peak count unavailable";
    const durationText = Number.isFinite(result.durationSeconds)
      ? formatCompactElapsedTime(result.durationSeconds)
      : "Duration unavailable";
    return [
      ["Test", resultRow?.test_name ?? "GAMI sweep"],
      ["Engine", identity.engineLabel],
      ["Window", windowText],
      ["Spread", `${spreadText}${result.spreadBand ? ` (${result.spreadBand})` : ""}`],
      ["Confidence", [titleCaseConfidence(result.confidence), result.confidenceDetail].filter(Boolean).join(" - ") || "Not stated"],
      ["Peaks", peaksText],
      ["Duration", `${durationText}${result.durationBand ? ` (${String(result.durationBand).replace("-", " ")})` : ""}`],
    ];
  }

  function renderSpecialTestsSection(writer, context) {
    const rows = (Array.isArray(context?.savedSpecialTests) ? context.savedSpecialTests : [])
      .filter((result) => result?.test_kind === "gami-sweep" || result?.testKind === "gami-sweep");
    if (!rows.length) return;
    const data = context?.flightData ?? [];
    writer.heading("Special Tests");
    rows.forEach((result, index) => {
      writer.keyValueGrid(gamiSpecialTestReportRows(result, data), { columns: 2, rowHeight: 34 });
      const peakRows = gamiPeakTableRows(result?.result_json ?? result?.result ?? {}, data);
      if (peakRows.length) {
        writer.subheading("Cylinder peaks");
        writer.simpleTable(
          ["Cyl", "Peak FF", "Peak EGT", "Time", "Cue"],
          peakRows,
          [50, 90, 90, 100, 174],
          { rowHeight: 22, scoreColumn: -1 },
        );
      }
      const savedAt = result.updated_at ?? result.created_at;
      if (savedAt) writer.keyValue("Saved result", savedAt);
      if (index < rows.length - 1) writer.gap(6);
    });
  }

  async function buildFlightReportPdfBlobV2() {
    const context = captureAssemblyContext();
    const { flightData, selectedFlight, sourceMonitor, aiSummaryState } = context;
    const summary = context.healthSummary ?? calculateHealthSummary(flightData);
    const first = flightData[0];
    const mapImage = await reportFlightMapImage();
    assertAssemblyContextCurrent(context);
    const writer = makePdfWriter({ profileDurationBuckets: getProfileDurationBuckets() });
    const aircraft = selectedFlight?.aircraft ?? "Sample";
    const monitor = selectedFlight?.monitor ?? sourceMonitor;
    const engines = currentEngineDescriptors();
    const reviewItems = reportReviewItems(summary);
    const primaryItem = reviewItems[0] ?? null;
    const primaryFinding = primaryItem?.finding ?? reportStandalonePrimaryFinding(summary);
    const primaryMetric = primaryItem?.metric ?? summary.overall;
    const primaryLesson = engineMetricLesson(primaryItem?.key ?? "overall", primaryMetric, primaryFinding);
    const status = engineTriageStatus(reviewItems, summary);
    const renderTopFindingSummary = () => {
      const headlineScore = Number.isFinite(primaryMetric?.score) ? primaryMetric.score : summary.overall.score;
      const headlineParagraph = reportFindingText(primaryFinding, 360)
        || primaryLesson?.meaning
        || "Engine review is clear at the current sensitivity setting.";
      writer.headlineSummary({
        statusLabel: status.label,
        statusClassName: status.className,
        score: headlineScore,
        title: primaryFinding?.title ?? "Engine review clear",
        paragraph: headlineParagraph,
        actions: sortReportActionsByPriority(primaryFinding?.actions ?? []),
      });
    };

    writer.cover({
      title: `${aircraft} Flight Report`,
      subtitle: `${flightDateLabel(first, selectedFlight)} - ${first && flightData.at(-1) ? `${formatPointTime(first)} to ${formatPointTime(flightData.at(-1))}` : "time unavailable"} - ${monitor}`,
      kicker: "Alary IQ advisory report",
      score: summary.overall.score,
      status: "Automated screening - not a maintenance diagnosis",
    });

    const aiSummaryPayload = aiSummaryPayloadJson(aiSummaryState?.payload, summary);
    const hasAiSummary = aiSummaryState?.status === "ready" && aiSummaryPayload;
    if (hasAiSummary) {
      const generatedLabel = aiSummaryGeneratedLabel(
        aiSummaryState.payload?.aiSummary ?? aiSummaryState.payload,
      );
      renderAiSummarySection(writer, aiSummaryPayload, generatedLabel, { healthSummary: summary });
      renderTopFindingSummary();
    } else {
      renderTopFindingSummary();
    }

    renderSpecialTestsSection(writer, context);

    writer.heading("Flight Summary");
    writer.keyValueGrid(flightReportRows(summary), { columns: 2 });
    const flightProfileRows = reportFlightProfileRows(flightData, phaseProfileRows(flightData), { mapImage });
    if (mapImage) {
      writer.flightMap(mapImage, reportFlightMapCaption(mapImage), reportFlightMapTiles(mapImage, flightData, phaseProfileRows(flightData)));
    } else if (flightProfileRows.length) {
      writer.heading("Flight Profile");
      writer.keyValueGrid(flightProfileRows, { columns: 2, rowHeight: 34 });
    }

    if (engines.length > 1) {
      writer.heading("Twin Engine Overview");
      writer.metricCards(reportMultiEngineCards(summary, engines));
      writer.simpleTable(
        ["Scope", "Score", "Primary cue", "Peak CHT", "Fuel avg", "RPM avg", "Oil P min"],
        reportMultiEngineOverviewRows(summary, engines),
        [72, 52, 146, 58, 58, 58, 60],
        { rowHeight: 26 },
      );
    }

    const traceRows = reportTraceRows(summary);
    const phaseRows = phaseProfileRows(flightData);
    const twinTraceRows = reportMultiEngineTraceRows(engines);
    const traceRowCount = traceRows.filter((row) => row.values?.some(Number.isFinite)).length;
    const twinTraceRowCount = twinTraceRows.filter((row) => row.values?.some(Number.isFinite)).length;
    writer.ensureSpace(35 + (twinTraceRowCount ? 30 + twinTraceRowCount * 34 + 22 : 0) + (traceRowCount ? 30 + traceRowCount * 34 + 22 : 0) + 72);
    writer.heading("Flight Chart Data");
    writer.sparklineRows("Twin Engine Trace Comparison", twinTraceRows);
    writer.sparklineRows("Logged Trace Highlights", traceRows);
    writer.phaseTimeline(phaseRows);
    if (typeof writer.overviewVisuals === "function") {
      writer.overviewVisuals(buildReportOverviewVisuals({
        flightData,
        summary,
        selectedFlight,
        engineDescriptors: engines,
      }));
    }

    const turboContext = reportTurboContext(summary);
    if (turboContext) {
      writer.ensureSpace(300);
      writer.heading("Turbo Context");
      writer.turboContext(turboContext);
    }

    writer.heading("All Checks At A Glance");
    writer.scoreBars(reportHealthCheckRows(summary));
    writeFuelPressureContextSection(writer, summary);

    writer.heading("Cylinder Summary");
    const cylinderRows = reportCylinderRows(summary);
    if (cylinderRows.length) {
      writer.simpleTable(
        ["Cylinder", "Score", "CHT max", "High CHT", "CHT vs others", "High EGT", "Cue"],
        reportCylinderTableRows(cylinderRows),
        [48, 52, 58, 58, 60, 58, 170],
        { rowHeight: 25 },
      );
    } else {
      writer.paragraph("No per-cylinder CHT or EGT channels were found in this log.");
    }

    const additionalFindings = reviewItems.slice(1, 6);
    writer.heading("Other Findings To Review");
    if (!reviewItems.length) {
      writer.paragraph("No active engine review cues at this sensitivity setting.");
    } else if (!additionalFindings.length) {
      writer.paragraph("No additional findings beyond the top finding above.");
    } else {
      additionalFindings.forEach((item) => {
        const lesson = engineMetricLesson(item.key, item.metric, item.finding);
        const causePresentation = findingCausePresentation(item.finding, item.metric);
        const whatWeSaw = reportFindingText(item.finding, 320);
        const contextParts = [reportWindowSummary(item.windows), item.finding?.meta || item.metric?.note]
          .map((value) => (typeof value === "string" ? value.trim() : value))
          .filter((value) => value && value !== "No marked window");
        writer.findingCard({
          label: item.label,
          title: item.finding?.title ?? item.metric?.note ?? "Review",
          score: item.metric?.score,
          statusLabel: findingDisplayLabel(item.finding, item.metric),
          whatWeSaw,
          contextLine: contextParts.join(" - ") || null,
          whyItMatters: lesson?.meaning,
          causes: item.finding?.causes ?? [],
          causesLabel: causePresentation.title,
          showCauseWeights: causePresentation.weighted,
          actions: sortReportActionsByPriority(item.finding?.actions ?? []),
        });
      });
    }

    const profileRows = operationalProfileReportRows(phaseRows, flightData);
    writer.ensureSpace(35 + Math.ceil(profileRows.length / 2) * 34 + 8);
    writer.heading("Operational Context");
    writer.keyValueGrid(profileRows, { columns: 2, rowHeight: 34 });

    writePropellerRpmContextSection(writer, summary);

    const qualityRows = dataQualityItems().map((item) => [item.label, item.value]);
    writer.ensureSpace(35 + Math.ceil(qualityRows.length / 2) * 38 + 8);
    writer.heading("Data Quality");
    writer.keyValueGrid(qualityRows, { columns: 2, rowHeight: 38 });

    renderSourceSection(writer, context);

    writer.gap(8);
    writer.shortDisclaimer("Automated screening, not a maintenance diagnosis. Findings here are starting points for review with a certified A&P or other qualified maintenance professional, who has the final word on any inspection or repair decision.");

    return reportIdentity(context, buildPdfBlob(writer.pages), reportFileName(selectedFlight, first, flightDateLabel));
  }

  function renderSourceSection(writer, context) {
    const sourceRows = Number(context.sourceRows ?? 0);
    const sourceRawRows = Number(context.sourceRawRows ?? 0);
    writer.heading("Source");
    writer.keyValue("Log", context.sourceName);
    writer.keyValue("Samples parsed", sourceRows.toLocaleString());
    if (sourceRawRows > sourceRows) writer.keyValue("Raw rows", sourceRawRows.toLocaleString());
    writer.keyValue("Channels used in scoring", rawChannelKeys().filter((key) => scoringRole(key) !== "No").join(", "));
  }

  async function buildFlightReportPdfBlobV1() {
    const context = captureAssemblyContext();
    const { flightData, selectedFlight, sourceMonitor } = context;
    const summary = context.healthSummary ?? calculateHealthSummary(flightData);
    const first = flightData[0];
    const mapImage = await reportFlightMapImage();
    assertAssemblyContextCurrent(context);
    const writer = makePdfWriter({ profileDurationBuckets: getProfileDurationBuckets() });
    const aircraft = selectedFlight?.aircraft ?? "Sample";
    const monitor = selectedFlight?.monitor ?? sourceMonitor;
    const engines = currentEngineDescriptors();
    const reportMetricRows = reportEngineMetricRows(summary);
    const reviewItems = reportReviewItems(summary);
    const primaryItem = reviewItems[0] ?? null;
    const primaryFinding = primaryItem?.finding ?? reportStandalonePrimaryFinding(summary);
    const primaryMetric = primaryItem?.metric ?? summary.overall;
    const primaryLesson = engineMetricLesson(primaryItem?.key ?? "overall", primaryMetric, primaryFinding);
    const status = engineTriageStatus(reviewItems, summary);

    writer.cover({
      title: `${aircraft} Flight Report`,
      subtitle: `${flightDateLabel(first, selectedFlight)} - ${first && flightData.at(-1) ? `${formatPointTime(first)} to ${formatPointTime(flightData.at(-1))}` : "time unavailable"} - ${monitor}`,
      kicker: "Alary IQ advisory report",
      score: summary.overall.score,
      status: "Automated screening - not a maintenance diagnosis",
    });

    writer.metricCards(reportAtAGlanceCards(summary, status, primaryFinding, primaryMetric));

    writer.heading("Flight Summary");
    writer.keyValueGrid(flightReportRows(summary), { columns: 2 });
    const flightProfileRows = reportFlightProfileRows(flightData, phaseProfileRows(flightData), { mapImage });
    if (mapImage) {
      writer.flightMap(mapImage, reportFlightMapCaption(mapImage), reportFlightMapTiles(mapImage, flightData, phaseProfileRows(flightData)));
    } else if (flightProfileRows.length) {
      writer.heading("Flight Profile");
      writer.keyValueGrid(flightProfileRows, { columns: 2, rowHeight: 34 });
    }

    if (engines.length > 1) {
      writer.heading("Twin Engine Overview");
      writer.metricCards(reportMultiEngineCards(summary, engines));
      writer.simpleTable(
        ["Scope", "Score", "Primary cue", "Peak CHT", "Fuel avg", "RPM avg", "Oil P min"],
        reportMultiEngineOverviewRows(summary, engines),
        [72, 52, 146, 58, 58, 58, 60],
        { rowHeight: 26 },
      );

      writer.heading("Twin Engine Primary Cues");
      writer.reviewCueCards(reportMultiEnginePrimaryCues(summary, engines));
    }

    const traceRows = reportTraceRows(summary);
    const phaseRows = phaseProfileRows(flightData);
    const twinTraceRows = reportMultiEngineTraceRows(engines);
    const traceRowCount = traceRows.filter((row) => row.values?.some(Number.isFinite)).length;
    const twinTraceRowCount = twinTraceRows.filter((row) => row.values?.some(Number.isFinite)).length;
    writer.ensureSpace(35 + (twinTraceRowCount ? 30 + twinTraceRowCount * 34 + 22 : 0) + (traceRowCount ? 30 + traceRowCount * 34 + 22 : 0) + 72);
    writer.heading("Flight Chart Data");
    writer.sparklineRows("Twin Engine Trace Comparison", twinTraceRows);
    writer.sparklineRows("Logged Trace Highlights", traceRows);
    writer.phaseTimeline(phaseRows);

    const turboContext = reportTurboContext(summary);
    if (turboContext) {
      writer.ensureSpace(300);
      writer.heading("Turbo Context");
      writer.turboContext(turboContext);
    }

    writer.heading("Health Checks");
    writer.scoreBars(reportHealthCheckRows(summary));
    writeFuelPressureContextSection(writer, summary);

    const primaryScore = Number.isFinite(primaryMetric?.score) ? primaryMetric.score : summary.overall.score;
    writer.ensureSpace(35 + 94);
    writer.heading("Engine Triage");
    writer.triageSummary({
      status: status.label,
      title: primaryFinding.title,
      score: primaryScore,
      scoreLabel: primaryItem?.label ?? summary.overall.label,
      evidence: primaryFinding.meta ?? primaryMetric?.note ?? "No evidence note",
      evidenceItems: reportFindingEvidenceItems(primaryFinding, primaryFinding.meta ?? primaryMetric?.note ?? ""),
      body: reportFindingText(primaryFinding, 620),
      lesson: primaryLesson,
      confidence: primaryFinding.confidence ?? "Not stated",
      actions: primaryFinding.actions ?? [],
    });

    const sensorChannelItems = reportSensorChannelItems(summary.sensors);
    if (sensorChannelItems.length) {
      writer.heading("Probe Channel Review");
      sensorChannelItems.forEach((item) => {
        writer.keyValue(item.label, item.evidence);
        writer.keyValue("Marked window", item.window);
        writer.paragraph(item.detail);
        writer.gap(3);
      });
    }

    writer.heading("Additional Engine Review Cues");
    const additionalItems = reviewItems.slice(1, 7);
    if (additionalItems.length) {
      writer.reviewCueCards(additionalItems.map((item) => ({
        label: item.label,
        title: item.finding?.title ?? item.metric.note ?? "Review",
        score: item.metric?.score,
        evidenceItems: reportFindingEvidenceItems(item.finding, item.finding?.meta ?? item.metric?.note ?? ""),
        window: reportWindowSummary(item.windows),
        body: reportFindingText(item.finding, 330),
        causes: item.finding?.causes ?? [],
        actions: item.finding?.actions ?? [],
      })));
    } else {
      writer.paragraph(reviewItems.length ? "No additional review cues were active beyond the primary finding." : "No active engine review cues at this sensitivity setting.");
    }

    writer.heading("Engine Concepts Behind The Checks");
    const conceptRows = (reviewItems.length ? reviewItems : reportMetricRows.slice(0, 5)).slice(0, 6);
    writer.conceptCards(conceptRows.map((item) => {
      const lesson = engineMetricLesson(item.key, item.metric, item.finding);
      return {
        title: lesson.title,
        label: item.label,
        note: item.metric?.note ?? item.finding?.meta ?? "",
        score: item.metric?.score,
        watches: lesson.watches,
        meaning: lesson.meaning,
        review: lesson.review,
      };
    }));

    writer.heading("Engine Evidence Windows");
    const windowRows = reviewItems
      .flatMap((item) => item.windows.map((window) => ({ item, window })))
      .slice(0, 10);
    if (windowRows.length) {
      windowRows.forEach(({ item, window }) => {
        const start = formatCompactElapsedTime(window.startTime);
        const end = Number.isFinite(window.endTime) ? formatCompactElapsedTime(window.endTime) : start;
        writer.bullet(`${item.label}: ${window.label ?? `${start}-${end}`} (${start}-${end})`);
      });
    } else {
      writer.paragraph("No specific flight segment was marked as supporting evidence for this log.");
    }

    writer.heading("Cylinder Summary");
    const cylinderRows = reportCylinderRows(summary);
    if (cylinderRows.length) {
      writer.simpleTable(
        ["Cylinder", "Score", "CHT max", "High CHT", "CHT vs others", "High EGT", "Cue"],
        reportCylinderTableRows(cylinderRows),
        [48, 52, 58, 58, 60, 58, 170],
        { rowHeight: 25 },
      );
    } else {
      writer.paragraph("No per-cylinder CHT or EGT channels were found in this log.");
    }

    writePrimaryFindingSections(writer, { finding: primaryFinding, primaryMetric });

    const profileRows = operationalProfileReportRows(phaseRows, flightData);
    writer.ensureSpace(35 + Math.ceil(profileRows.length / 2) * 34 + 8);
    writer.heading("Operational Context");
    writer.keyValueGrid(
      profileRows,
      { columns: 2, rowHeight: 34 },
    );

    writePropellerRpmContextSection(writer, summary);

    const qualityRows = dataQualityItems().map((item) => [item.label, item.value]);
    writer.ensureSpace(35 + Math.ceil(qualityRows.length / 2) * 38 + 8);
    writer.heading("Data Quality");
    writer.keyValueGrid(qualityRows, { columns: 2, rowHeight: 38 });

    renderSourceSection(writer, context);

    writer.heading("Automated Analysis Disclaimer");
    getReportDisclaimerParagraphs().forEach((paragraph) => writer.paragraph(paragraph));

    return reportIdentity(context, buildPdfBlob(writer.pages), reportFileName(selectedFlight, first, flightDateLabel));
  }

  async function buildFlightReportPdfBlob() {
    return buildFlightReportPdfBlobV2();
  }

  return {
    renderAiSummarySection,
    buildFlightReportPdfBlob,
    buildFlightReportPdfBlobV1,
    buildFlightReportPdfBlobV2,
  };
}
